import { useState } from "react";
import { useGT } from "gt-react";
import { Check, Copy, RefreshCw, Server, ShieldCheck, Sparkles, X } from "lucide-react";
import { useAppStore, refreshRuntimeStatus, useRuntimeStore } from "../store";
import { clearRuntimePairingToken, completeRuntimePairing, getRuntimeBaseUrl } from "../runtimeClient";
import { toast } from "./Toasts";

export function LocalRuntimeDialog() {
  const t = useGT();
  const showRuntimeDialog = useAppStore((state) => state.showRuntimeDialog);
  const setShowRuntimeDialog = useAppStore((state) => state.setShowRuntimeDialog);
  const reachable = useRuntimeStore((state) => state.reachable);
  const paired = useRuntimeStore((state) => state.paired);
  const version = useRuntimeStore((state) => state.version);
  const agentProviders = useRuntimeStore((state) => state.agentProviders);
  const checking = useRuntimeStore((state) => state.checking);
  const [pairCode, setPairCode] = useState("");
  const [pairing, setPairing] = useState(false);
  const runtimeBaseUrl = getRuntimeBaseUrl();

  if (!showRuntimeDialog) {
    return null;
  }

  const handleRefresh = async () => {
    await refreshRuntimeStatus();
  };

  const handlePair = async () => {
    if (!pairCode.trim()) return;
    setPairing(true);
    try {
      await completeRuntimePairing({ code: pairCode.trim() });
      setPairCode("");
      await refreshRuntimeStatus();
      toast(t("Local Runtime paired"), "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : t("Failed to pair runtime"), "error");
    } finally {
      setPairing(false);
    }
  };

  const handleForgetPairing = async () => {
    clearRuntimePairingToken();
    await refreshRuntimeStatus();
    toast(t("Local browser pairing cleared"), "success");
  };

  const handleCopyCommand = async (command: string) => {
    await navigator.clipboard.writeText(command);
    toast(t("Command copied"), "success");
  };

  const statusLabel = reachable
    ? paired ? t("Paired") : t("Found · Not Paired")
    : t("Not Found");

  const statusColor = reachable
    ? paired ? "text-success" : "text-warning"
    : "text-zinc-500";

  const statusDot = reachable
    ? paired ? "bg-success" : "bg-warning"
    : "bg-zinc-600";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && setShowRuntimeDialog(false)}
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <Server size={14} className="text-zinc-400" />
            <div>
              <h2 className="text-sm font-semibold text-zinc-200">Local Runtime</h2>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusDot}`} />
                <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={checking}
              className="rounded border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-zinc-400 hover:bg-surface-raised hover:text-zinc-200 disabled:opacity-40"
            >
              {checking ? t("Checking...") : t("Refresh")}
            </button>
            <button
              onClick={() => setShowRuntimeDialog(false)}
              className="rounded p-1 text-zinc-500 hover:bg-surface hover:text-zinc-300"
              title={t("Close")}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5">

          {/* ── State: Not found → Install/Start guidance ── */}
          {!reachable && (
            <>
              <div className="rounded-xl border border-border bg-surface px-4 py-4">
                <p className="text-sm text-zinc-300">
                  {t("No runtime found at {url}. Install and start it to unlock local execution.", { url: runtimeBaseUrl })}
                </p>
              </div>

              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-surface px-3 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t("1. Install CLI")}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 rounded bg-surface-raised px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                    <span>brew install sebastian-breitzke/tap/spuerhund</span>
                    <button
                      type="button"
                      onClick={() => void handleCopyCommand("brew install sebastian-breitzke/tap/spuerhund")}
                      className="rounded p-1 text-zinc-500 hover:bg-surface hover:text-zinc-200"
                      title={t("Copy command")}
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] leading-5 text-zinc-500">
                    {t("Windows, Linux, or prefer npx?")}{" "}
                    <a href="https://spuerhund.dev/download" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-light">
                      {t("All download options")}
                    </a>
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-surface px-3 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t("2. Install background service")}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 rounded bg-surface-raised px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                    <span>spuerhund runtime install-service</span>
                    <button
                      type="button"
                      onClick={() => void handleCopyCommand("spuerhund runtime install-service")}
                      className="rounded p-1 text-zinc-500 hover:bg-surface hover:text-zinc-200"
                      title={t("Copy command")}
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] leading-5 text-zinc-500">
                    {t("On macOS this installs a LaunchAgent so the runtime starts automatically after login.")}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-surface px-3 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t("Or start manually")}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 rounded bg-surface-raised px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                    <span>spuerhund runtime start</span>
                    <button
                      type="button"
                      onClick={() => void handleCopyCommand("spuerhund runtime start")}
                      className="rounded p-1 text-zinc-500 hover:bg-surface hover:text-zinc-200"
                      title={t("Copy command")}
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-surface px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{t("Why Local Runtime")}</div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-border bg-surface-raised px-3 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-200">
                      <ShieldCheck size={13} />
                      {t("No CORS friction")}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-zinc-500">
                      {t("Requests run locally, so APIs do not need to allow browser-origin requests.")}
                    </p>
                  </div>
                  <div className="rounded-lg border border-border bg-surface-raised px-3 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-zinc-200">
                      <Sparkles size={13} />
                      {t("Future automation path")}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-zinc-500">
                      {t("This is the path for later AI- and CLI-driven automation without going through browser limits.")}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── State: Found, not paired → Pairing input ── */}
          {reachable && !paired && (
            <>
              <div className="rounded-xl border border-warning/30 bg-warning-bg px-4 py-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-warning">{t("Pair this browser")}</div>
                <p className="mb-3 text-sm text-zinc-300">
                  {t("Run")} <span className="rounded bg-surface px-1.5 py-0.5 font-mono text-xs text-zinc-100">spuerhund runtime pair</span> {t("in your terminal, then paste the code here.")}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={pairCode}
                    onChange={(event) => setPairCode(event.target.value.toUpperCase())}
                    placeholder={t("Pairing Code")}
                    className="min-w-[220px] flex-1 rounded bg-surface-raised px-3 py-1.5 text-sm font-mono uppercase tracking-wide text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    onKeyDown={(event) => event.key === "Enter" && void handlePair()}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => void handlePair()}
                    disabled={!pairCode.trim() || pairing}
                    className="inline-flex items-center gap-1 rounded bg-brand px-3 py-1.5 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:opacity-40"
                  >
                    <Check size={12} />
                    {pairing ? t("Pairing...") : t("Pair")}
                  </button>
                </div>
              </div>
              <div className="text-xs text-zinc-500">
                {version || "Local Runtime"} {t("on")} {runtimeBaseUrl}{agentProviders.length ? ` · Providers: ${agentProviders.join(", ")}` : ""}
              </div>
            </>
          )}

          {/* ── State: Found + paired → Connected status ── */}
          {reachable && paired && (
            <>
              <div className="rounded-xl border border-success/30 bg-success-bg px-4 py-4">
                <p className="text-sm text-success">
                  {t("Requests and schema refresh use the Local Runtime automatically.")}
                </p>
                <div className="mt-2 text-xs text-success/70">
                  {version || "Local Runtime"} {t("on")} {runtimeBaseUrl}{agentProviders.length ? ` · Providers: ${agentProviders.join(", ")}` : ""}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-4">
                <div className="text-xs text-zinc-500">
                  {t("Clearing the pairing only affects this browser. The runtime keeps running.")}
                </div>
                <button
                  type="button"
                  onClick={() => void handleForgetPairing()}
                  className="shrink-0 rounded border border-border bg-surface-raised px-3 py-1.5 text-xs font-medium text-zinc-300 hover:bg-surface"
                >
                  {t("Forget Pairing")}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
